'use client'

import { ControllerRenderProps, Path } from 'react-hook-form'

import { getUserFilterMetadata, ListUsersParams } from '@api/orval/apiHooks'
import { AllFiltersArea, FieldConfig } from '@components/Filters/filter.types'
import { leftSideGroupStyle, rightSideGroupStyle } from '@components/Filters/FilterForm'
import { Input, MultiSelect, SingleSelect } from '@fp/ui-kit'
import { IS_TRADIN } from '@lib/constants'
import { convertToValueLabel } from '@lib/convertToValueLabel'
import { getInputValue } from '@lib/getFilterInputValue'
import type { IsoDateRange } from '@lib/tz'
import { DatePicker } from '@ui/DateRangePicker'

export type UsersFormValues = {
  search_field?: string
  kyc_name?: string
  email?: string
  kycaid_verification_status?: string[]
  country?: string[]
  tag_list?: string[]
  brand_id?: string | null
  created_at?: IsoDateRange
  trust_profile_score_from?: number
  trust_profile_score_to?: number
}

type OptionFieldKeys = 'kycaid_verification_status' | 'country' | 'tags' | 'brands'

export const usersConfig = {
  filterTitle: 'Filters',
  filtersArea: 'users' as AllFiltersArea,
  initialValues: {
    search_field: '',
    email: '',
    kyc_name: '',
    kycaid_verification_status: [],
    country: [],
    tag_list: [],
    brand_id: null,
    created_at: { from: undefined, to: undefined },
    trust_profile_score_from: undefined,
    trust_profile_score_to: undefined,
  },
  loadOptions: async (query: ListUsersParams) => {
    const data = await getUserFilterMetadata(query as ListUsersParams)

    return {
      count: data?.count,
      options: {
        kycaid_verification_status: (data?.fields.kycaid_verification_statuses || []).map(convertToValueLabel),
        country: Object.keys(data?.fields.countries || {}).map((country: string) => ({
          value: (data?.fields.countries as Record<string, string>)?.[country] ?? '',
          label: country,
        })),
        tags: (data?.fields.tags || []).map(convertToValueLabel),
        brands: (data?.fields.brands || []).map((b) => ({ value: String(b.value), label: b.name })),
      },
    }
  },
  fields: (options: Record<OptionFieldKeys, { value: string; label: string }[]>): FieldConfig<UsersFormValues>[] => [
    {
      name: 'search_field',
      label: 'Search',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) => (
        <Input
          {...fieldProps}
          placeholder='Search'
          value={typeof fieldProps.value === 'string' ? fieldProps.value : ''}
        />
      ),
    },
    {
      name: 'email',
      label: 'Email',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) => (
        <Input
          {...fieldProps}
          placeholder='Search for email'
          value={typeof fieldProps.value === 'string' ? fieldProps.value : ''}
        />
      ),
    },
    {
      name: 'kyc_name',
      label: 'KYC name',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) => (
        <Input
          {...fieldProps}
          placeholder='Search for KYC name'
          value={typeof fieldProps.value === 'string' ? fieldProps.value : ''}
        />
      ),
    },

    {
      name: 'kycaid_verification_status',
      label: 'KYC Status',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) =>
        Array.isArray(options.kycaid_verification_status) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.kycaid_verification_status}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },
    {
      name: 'country',
      label: 'Country',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) =>
        Array.isArray(options.country) && (
          <MultiSelect
            {...fieldProps}
            placeholder='All'
            options={options.country}
            onValueChange={fieldProps.onChange}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },
    {
      name: 'tag_list',
      label: 'Tags',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) =>
        Array.isArray(options.tags) && (
          <MultiSelect
            {...fieldProps}
            onValueChange={fieldProps.onChange}
            options={options.tags}
            value={(fieldProps.value as string[]) || []}
          />
        ),
    },
    {
      name: 'brand_id',
      label: 'Brand',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) =>
        Array.isArray(options.brands) && (
          <SingleSelect
            options={options.brands}
            placeholder='Select brand'
            value={(fieldProps.value as string) ?? ''}
            onChange={fieldProps.onChange}
            isToggleEnabled
          />
        ),
    },

    {
      name: 'created_at',
      label: 'Started at from - to',
      element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) => (
        <DatePicker value={fieldProps.value as IsoDateRange} onChange={fieldProps.onChange} />
      ),
    },
    ...(IS_TRADIN
      ? [
          {
            name: 'trust_profile_score_from',
            label: 'Trust profile score',
            group: 'trust_profile_score',
            element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) => (
              <Input
                {...fieldProps}
                className={leftSideGroupStyle}
                placeholder='Min'
                type='number'
                value={getInputValue(fieldProps.value)}
              />
            ),
          } as FieldConfig<UsersFormValues>,
          {
            name: 'trust_profile_score_to',
            label: '',
            group: 'trust_profile_score',
            element: (fieldProps: ControllerRenderProps<UsersFormValues, Path<UsersFormValues>>) => (
              <Input
                {...fieldProps}
                className={rightSideGroupStyle}
                placeholder='Max'
                type='number'
                value={getInputValue(fieldProps.value)}
              />
            ),
          } as FieldConfig<UsersFormValues>,
        ]
      : []),
  ],
}
